import { createId } from '@paralleldrive/cuid2'
import type { HarmonyPackageFolder } from '@rhythmic/types'
import { HarmonyPackageFolderSchema } from '@rhythmic/types'
import type { Collection, Transaction } from '@tanstack/vue-db'
import { createOptimisticAction } from '@tanstack/vue-db'
import { useRhythmicLogger } from '#imports'

import { getHarmonyFolderCollection } from '../getCollections'

const logger = useRhythmicLogger('useHarmonyPlanContext:createFolder')

export interface CreateHarmonyPlanFolderPayload {
  name: string
  description?: string | null
  parentId?: string | null
}

export interface HarmonyPlanFolderMutationContext {
  planId: string
  workspaceId: string
  collection?: Collection<HarmonyPackageFolder>
}

function validateParams(payload: CreateHarmonyPlanFolderPayload, context: HarmonyPlanFolderMutationContext) {
  if (!payload.name || payload.name.trim().length === 0)
    throw new Error('Folder name is required to create a harmony plan folder')

  const { planId, workspaceId } = context

  if (!planId) throw new Error('Harmony plan identifier is required to create a folder')

  if (!workspaceId) throw new Error('Workspace identifier is required to create a folder')

  return { planId, workspaceId }
}

export function handleCreateHarmonyPlanFolder(
  payload: CreateHarmonyPlanFolderPayload,
  context: HarmonyPlanFolderMutationContext,
): Transaction {
  const { planId, workspaceId } = validateParams(payload, context)
  const collection = getHarmonyFolderCollection(planId)
  const folderId = `folder_${createId()}`

  const createFolderTx = createOptimisticAction<{
    payload: CreateHarmonyPlanFolderPayload
    context: HarmonyPlanFolderMutationContext
  }>({
    onMutate: (mutation) => {
      const optimisticFolder = HarmonyPackageFolderSchema.parse({
        id: folderId,
        name: mutation.payload.name.trim(),
        description: mutation.payload.description ?? null,
        package_id: planId,
        parent_id: mutation.payload.parentId ?? null,
        workspace_id: workspaceId,
        created_at: new Date(),
        updated_at: new Date(),
      })

      logger.debug('Optimistically inserting harmony plan folder', {
        folderId,
        optimisticFolder,
      })
      collection.insert(optimisticFolder)
    },
    mutationFn: async (mutation) => {
      const body = {
        id: folderId,
        name: mutation.payload.name.trim(),
        description: mutation.payload.description,
        parent_id: mutation.payload.parentId ?? null,
        package_id: planId,
        workspace_id: workspaceId,
      }

      logger.debug('Persisting harmony plan folder via API', {
        folderId,
        body,
      })
      const result = await $fetch<{ txid?: string | number }>('/api/harmony-package-folders', {
        method: 'POST',
        body,
      })

      if (result?.txid && collection.utils.awaitTxId) {
        logger.debug('Awaiting transaction confirmation for folder create', {
          txid: result.txid,
        })
        await collection.utils.awaitTxId(Number.parseInt(result.txid.toString()))
      }
    },
  })

  const transaction = createFolderTx({ payload, context })
  transaction.metadata = {
    ...transaction.metadata,
    folderId,
  }
  return transaction
}
